<template>
  <AuthLayout>
    <template #form>
      <!-- Loading State -->
      <LoadingScreen v-if="loading" color="var(--purple)" />

      <!-- Error State -->
      <div v-else-if="error" class="consent-error">
        <AuthFormHeader title="Authorization Failed" :subtitle="errorMessage" />
        <an-link @click="goBack" class="auth-link-secondary"> Go Back </an-link>
      </div>

      <!-- Consent Form -->
      <div v-else class="consent-form">
        <AuthFormHeader
          title="Authorization Request"
          subtitle="An application is requesting access to your Anima account"
        />

        <!-- Request Info -->
        <div class="consent-info">
          <div class="info-item">
            <span class="info-label">Application</span>
            <span class="info-value client-app">
              <img v-if="clientIcon" :src="clientIcon" :alt="clientName" class="client-icon" />
              {{ clientName || 'Unknown Application' }}
            </span>
          </div>
          <div v-if="!isAgentGridConsent" class="info-item figma-status">
            <span class="info-label">Figma Integration</span>
            <span class="info-value">
              <span v-if="hasFigmaIntegration" class="status-badge connected">
                <svg-icon name="circle-check" :size="14" />
                Connected
              </span>
              <div v-else class="figma-connect-wrapper">
                <button
                  type="button"
                  class="connect-figma-btn"
                  title="Required for importing Figma designs"
                  @click="connectFigma"
                >
                  <img :src="require('@/assets/svg/code-icons/figma.svg')" width="16" height="16" />
                  Connect Figma
                </button>
                <span class="figma-helper-text">
                  {{ isFigmaTokenExpired ? 'Your Figma session has expired' : 'Required for importing Figma designs' }}
                </span>
              </div>
            </span>
          </div>
        </div>

        <!-- Team Selection -->
        <div class="team-selection">
          <label class="team-label">Select a team</label>
          <an-select v-model="selectedTeamId" :options="teamOptions" placeholder="Select a team" />
        </div>

        <!-- Agent settings — optional. Collapsed = connect with defaults
             (the form sends no selection; the server resolves the default
             workspace + your full delegable capabilities). Scopes apply to
             the team's default workspace (workspace picker returns with
             multi-workspace support). -->
        <div class="agent-settings">
          <button type="button" class="agent-settings-toggle" @click="agentSettingsOpen = !agentSettingsOpen">
            <span>Agent settings <span class="toggle-optional">optional</span></span>
            <span class="toggle-chevron" :class="{ open: agentSettingsOpen }"></span>
          </button>

          <p v-if="!agentSettingsOpen" class="field-hint agent-settings-hint">
            Connecting as <strong>{{ clientName || 'this application' }}</strong> with default access to your workspace
            — expand to name or restrict this agent.
          </p>

          <div v-if="agentSettingsOpen" class="agent-settings-body">
            <div class="agent-name-section">
              <label class="section-label">
                Agent name
                <span
                  class="info-tip"
                  tabindex="0"
                  data-tip="Naming this connection creates an agent you can scope and revoke independently. The same name reconnects to the same agent; a new name creates a separate one."
                  >i</span
                >
              </label>
              <input
                v-model="agentName"
                class="agent-name-input"
                type="text"
                maxlength="120"
                :placeholder="clientName || 'Agent name'"
              />
            </div>

            <div class="workspace-access">
              <label class="section-label">
                Permissions
                <span
                  class="info-tip"
                  tabindex="0"
                  data-tip="What this agent may do in your team's default workspace. It can never do more than you can — and you can revoke it anytime."
                  >i</span
                >
              </label>

              <div v-if="workspacesLoading" class="workspace-loading">Loading…</div>
              <div v-else-if="workspacesError" class="workspace-error">{{ workspacesError }}</div>
              <template v-else>
                <div class="capability-checkboxes">
                  <label
                    v-for="capability in allCapabilities"
                    :key="capability"
                    class="capability-checkbox"
                    :class="{ disabled: !isCapabilityEnabled(capability) }"
                  >
                    <input
                      type="checkbox"
                      :value="capability"
                      v-model="selectedCapabilities"
                      :disabled="!isCapabilityEnabled(capability)"
                    />
                    <span>{{ capabilityLabels[capability] }}</span>
                  </label>
                </div>
              </template>
            </div>
          </div>
        </div>

        <!-- Consent Form (native HTML form for redirect) -->
        <form ref="consentForm" method="POST" :action="consentEndpoint" class="consent-actions">
          <input type="hidden" name="tx" :value="txId" />
          <input type="hidden" name="team_id" :value="selectedTeamId" />
          <input type="hidden" name="agent_name" :value="agentName || clientName" />
          <input type="hidden" name="authorization_details" :value="authorizationDetailsField" />

          <an-button
            type="submit"
            name="action"
            value="approve"
            variant="purple"
            size="lg"
            :disabled="!canApprove"
            :rounded="false"
          >
            Approve
          </an-button>

          <button type="submit" name="action" value="deny" class="deny-link">Deny</button>
        </form>

        <p class="consent-disclaimer">
          By approving, you allow this application to access your Anima account on behalf of the selected team.
        </p>
      </div>
    </template>
  </AuthLayout>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import axios from 'axios';
import AuthLayout from '@/components/Auth/AuthLayout';
import AuthFormHeader from '@/components/Auth/AuthFormHeader';
import LoadingScreen from '@/components/Loading/LoadingScreen';
import AnSelect from '@/components/Select/Select';
import auth from '@/auth';
import { readCookie } from '@/utils/cookie';
import { isAgentGridSource } from '@/utils/authBrand';

export default {
  name: 'OAuthConsent',
  components: {
    AuthLayout,
    AuthFormHeader,
    LoadingScreen,
    'an-select': AnSelect
  },
  data() {
    return {
      loading: true,
      error: false,
      errorMessage: '',
      txId: '',
      clientId: '',
      clientName: '',
      selectedTeamId: '',
      agentName: '',
      agentSettingsOpen: true,
      allCapabilities: ['read', 'write', 'share', 'publish'],
      capabilityLabels: { read: 'Read', write: 'Write', share: 'Share', publish: 'Publish' },
      workspaces: [],
      offerableCapabilities: [],
      selectedWorkspaceIds: [],
      selectedCapabilities: [],
      workspacesLoading: false,
      workspacesError: ''
    };
  },
  computed: {
    ...mapGetters('teamMemberships', ['userTeams']),
    ...mapGetters('users', ['figmaAuthToken', 'figmaTokenExpiresAt']),
    isAgentGridConsent() {
      return isAgentGridSource(this.$route?.query);
    },
    isFigmaTokenExpired() {
      return this.figmaAuthToken && this.figmaTokenExpiresAt && new Date(this.figmaTokenExpiresAt) <= new Date();
    },
    hasFigmaIntegration() {
      return !!this.figmaAuthToken && !this.isFigmaTokenExpired;
    },
    teamOptions() {
      return this.userTeams.map((team) => ({
        label: team.name,
        value: team.id
      }));
    },
    authorizationDetailsField() {
      // Collapsed section = send nothing: the server resolves the default
      // workspace + the full delegable capability set (one-click approve).
      if (!this.agentSettingsOpen) return '';
      if (!this.selectedWorkspaceIds.length || !this.selectedCapabilities.length) return '';
      return JSON.stringify([
        {
          type: 'grids_capability',
          capabilities: this.selectedCapabilities,
          boundary: { workspaceIds: this.selectedWorkspaceIds }
        }
      ]);
    },
    canApprove() {
      if (!this.selectedTeamId) return false;
      if (!this.agentSettingsOpen) return true; // defaults apply
      return this.selectedWorkspaceIds.length > 0 && this.selectedCapabilities.length > 0;
    },
    consentEndpoint() {
      const baseUrl = process.env.VUE_APP_PUBLIC_API_URL;
      return `${baseUrl}/oauth/consent`;
    },
    clientIcon() {
      // Map known MCP client names to their icons
      // Special case: "Claude Code" can have different suffixes (e.g., "Claude Code (anima)")
      if (this.clientName && this.clientName.startsWith('Claude Code')) {
        return require('@/assets/icons/mcp-clients/claude.svg');
      }

      const clientIcons = {
        'Visual Studio Code': require('@/assets/icons/mcp-clients/vscode.svg'),
        Cursor: require('@/assets/icons/mcp-clients/cursor.svg'),
        'Claude Code': require('@/assets/icons/mcp-clients/claude.svg')
      };
      return clientIcons[this.clientName] || null;
    }
  },
  watch: {
    selectedTeamId() {
      this.fetchConsentWorkspaces();
    }
  },
  async mounted() {
    // Check if user is authenticated
    if (!auth.isLoggedIn()) {
      // Redirect to login with return URL
      const currentUrl = window.location.href;
      this.$router.push({
        name: 'login',
        query: { redirect: currentUrl }
      });
      return;
    }

    // Get tx from query params
    this.txId = this.$route.query.tx;

    if (!this.txId) {
      this.error = true;
      this.errorMessage = 'Missing authorization request. Please start the OAuth flow again.';
      this.loading = false;
      return;
    }

    try {
      // Fetch tx info from AS
      await this.fetchTxInfo();

      // Fetch user's teams
      await this.fetchAllUserMemberships({ id: 'me', skipCache: true });

      // Pre-select current team from cookie if available
      const currentTeamId = readCookie('anima_currentTeamId');
      if (currentTeamId && this.userTeams.some((t) => t.id === currentTeamId)) {
        this.selectedTeamId = currentTeamId;
      } else if (this.userTeams.length > 0) {
        // Default to first team
        this.selectedTeamId = this.userTeams[0].id;
      }

      // Setting selectedTeamId (always changing from '') fires the watcher,
      // which loads the workspaces — no explicit fetch needed here. The panel
      // shows its own `workspacesLoading` state until that resolves.

      this.loading = false;
    } catch (err) {
      console.error('[OAuth Consent] Error:', err);
      this.error = true;
      this.errorMessage =
        err.response?.data?.error_description ||
        'Authorization request has expired or is invalid. Please start the OAuth flow again.';
      this.loading = false;
    }
  },
  methods: {
    ...mapActions('teamMemberships', ['fetchAllUserMemberships']),
    ...mapActions('figmaOAuth', ['generateFigmaOAuthUrl']),

    async fetchTxInfo() {
      const baseUrl = process.env.VUE_APP_PUBLIC_API_URL;
      const response = await axios.get(`${baseUrl}/oauth/tx-info`, {
        params: { tx: this.txId }
      });

      this.clientId = response.data.clientId;
      this.clientName = response.data.clientName;
    },

    isCapabilityEnabled(capability) {
      // Only capabilities the consenting human themselves holds are offerable.
      // (Client-requested ceilings — RFC 9396 on /authorize — are deferred to
      // the step-up re-consent phase; the MVP client asks for nothing.)
      return this.offerableCapabilities.includes(capability);
    },

    async fetchConsentWorkspaces() {
      if (!this.selectedTeamId) return;
      this.workspacesLoading = true;
      this.workspacesError = '';
      try {
        const baseUrl = process.env.VUE_APP_PUBLIC_API_URL;
        const response = await axios.get(`${baseUrl}/v1/grids/consent-workspaces`, {
          params: { team_id: this.selectedTeamId },
          headers: { Authorization: `Bearer ${auth.getToken()}` }
        });
        this.workspaces = response.data.workspaces || [];
        this.offerableCapabilities = response.data.offerableCapabilities || [];
        // Defaults (one-click approve): the default workspace pre-selected,
        // every offerable capability pre-checked.
        const defaults = this.workspaces.filter((w) => w.isDefault).map((w) => w.id);
        this.selectedWorkspaceIds = defaults.length ? defaults : this.workspaces.slice(0, 1).map((w) => w.id);
        this.selectedCapabilities = this.allCapabilities.filter((cap) => this.isCapabilityEnabled(cap));
      } catch (err) {
        console.error('[OAuth Consent] Failed to load workspaces:', err);
        this.workspaces = [];
        this.selectedWorkspaceIds = [];
        this.selectedCapabilities = [];
        this.workspacesError = 'Could not load workspaces for this team.';
      } finally {
        this.workspacesLoading = false;
      }
    },

    goBack() {
      // Go back or redirect to home
      if (window.history.length > 1) {
        this.$router.go(-1);
      } else {
        this.$router.push({ name: 'root' });
      }
    },

    async connectFigma() {
      // Use current URL (with tx param) as callback so user returns here after Figma auth
      const callbackUrl = window.location.href;
      const figmaOAuthUrl = await this.generateFigmaOAuthUrl({ callbackUrl });
      window.location.href = figmaOAuthUrl;
    }
  }
};
</script>

<style lang="scss" scoped>
@import '@/styles/_mixins.scss';
@import '@/styles/_auth.scss';
@import '@/styles/_auth-tokens.scss';

// The shared auth layout vertically centers its column, which reads as
// excessive top padding on this (taller) view — anchor it to the top instead.
// Scoped to the consent screen only; login/signup keep their centering.
::v-deep .auth-page-wrapper {
  align-items: flex-start;
  padding-top: 32px;
}

.consent-form {
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 400px;
  gap: 16px;
  // The auth layout vertically centers a full-viewport container with no
  // inner scrolling; when the (expanded) form outgrows the viewport it must
  // scroll itself.
  max-height: calc(100vh - 48px);
  overflow-y: auto;
  // Scrollable but with the scrollbar hidden (all engines).
  scrollbar-width: none; // Firefox
  -ms-overflow-style: none; // legacy Edge
  &::-webkit-scrollbar {
    display: none; // WebKit
  }

  > :first-child {
    margin-bottom: 0;
  }
}

.consent-error {
  display: flex;
  flex-direction: column;
  gap: $auth-gap-lg;
  width: 100%;
  max-width: 400px;

  .auth-link-secondary {
    text-align: center;
  }
}

.consent-info {
  display: flex;
  flex-direction: column;
  gap: $auth-gap-sm;
  padding: $auth-gap-md;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;

  .info-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .info-label {
    color: var(--transparent-white-50);
    font-size: 14px;
  }

  .info-value {
    color: var(--white);
    font-size: 14px;
    font-weight: 500;

    &.client-app {
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .client-icon {
      width: 20px;
      height: 20px;
      object-fit: contain;
    }
  }
}

.team-selection {
  display: flex;
  flex-direction: column;
  gap: 8px;

  .team-label {
    color: var(--transparent-white-70);
    font-size: 14px;
    font-weight: 500;
  }

  .an-select {
    ::v-deep .selected {
      background: rgba(255, 255, 255, 0.05);
      border: 1px solid rgba(255, 255, 255, 0.1);
      color: var(--white);
      height: 48px;
      border-radius: 6px;

      &.placeholder {
        color: var(--transparent-white-50);
      }

      &::after {
        filter: invert(1);
      }
    }

    ::v-deep .options {
      background: var(--dark-card-background);
      border-color: rgba(255, 255, 255, 0.1);
      color: var(--white);

      .option {
        color: var(--white);

        &:hover {
          background: rgba(255, 255, 255, 0.1);
        }
      }
    }
  }
}

.consent-actions {
  display: flex;
  flex-direction: column;
  align-items: center;
  // Approve + Deny read as one footer group.
  gap: 2px;
  margin-top: 4px;

  .an-button {
    width: 100%;
  }

  .deny-link {
    background: none;
    border: none;
    padding: 8px;
    cursor: pointer;
    color: var(--transparent-white-50);
    font-family: inherit;
    font-size: 14px;
    font-weight: 400;
    text-decoration: underline;
    transition: color 0.2s ease;

    &:hover {
      color: var(--transparent-white-70);
    }
  }
}

.agent-settings {
  // Filled container matching the team-select dropdown chrome.
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 6px;
  display: flex;
  flex-direction: column;
}

.agent-settings-toggle {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: 48px;
  // Right padding 0: the 32px chevron box hugs the container edge exactly
  // like the team dropdown's caret, so the two arrows align vertically.
  padding: 0 0 0 16px;
  background: none;
  border: none;
  cursor: pointer;
  color: #fff;
  font-family: inherit;
  font-size: 14px;
  font-weight: 500;
  text-align: left;

  // No focus ring on mouse click; keep a subtle one for keyboard navigation.
  &:focus {
    outline: none;
  }
  &:focus-visible {
    outline: 1px solid rgba(255, 255, 255, 0.3);
    outline-offset: -1px;
    border-radius: 6px;
  }

  .toggle-chevron {
    // Same caret as the team dropdown (an-select): chevron-down.svg, inverted
    // to white for the dark theme.
    display: inline-block;
    width: 32px;
    height: 32px;
    background-image: url('~@/assets/svg/chevron-down.svg');
    background-repeat: no-repeat;
    background-position: center;
    filter: invert(1);
    transition: transform 0.15s ease;

    &.open {
      transform: rotate(180deg);
    }
  }

  .toggle-optional {
    color: rgba(255, 255, 255, 0.4);
    font-size: 11px;
    font-weight: 400;
    margin-left: 6px;
  }
}

.agent-settings-hint {
  padding: 0 16px 12px;
}

.agent-settings-body {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 10px 16px 14px;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
}

.section-label {
  display: flex;
  align-items: center;
  gap: 6px;
  color: rgba(255, 255, 255, 0.7);
  font-size: 14px;
  font-weight: 500;
}

.info-tip {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 14px;
  height: 14px;
  border: 1px solid rgba(255, 255, 255, 0.35);
  border-radius: 50%;
  color: rgba(255, 255, 255, 0.55);
  font-size: 9px;
  font-style: italic;
  font-family: Georgia, serif;
  cursor: help;
  user-select: none;

  &:focus {
    outline: none;
  }

  // Tooltip bubble (hover or keyboard focus)
  &:hover::after,
  &:focus::after {
    content: attr(data-tip);
    position: absolute;
    bottom: calc(100% + 6px);
    // Anchored to the icon's left and growing rightward — centering pushed
    // the bubble past the card edge, where the scroll container clipped it.
    left: -4px;
    background: #2b2b30;
    border: 1px solid rgba(255, 255, 255, 0.12);
    border-radius: 6px;
    padding: 6px 10px;
    color: rgba(255, 255, 255, 0.85);
    font-size: 11px;
    font-style: normal;
    // Explicit app font — `inherit` would resolve to the icon's serif glyph font.
    font-family: $auth-body-font;
    font-weight: 400;
    line-height: 1.4;
    white-space: normal;
    width: max-content;
    max-width: 240px;
    z-index: 10;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
    pointer-events: none;
  }
}

.agent-name-section,
.workspace-access {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.agent-name-input {
  background: rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: var(--white);
  height: 48px;
  border-radius: 6px;
  padding: 0 12px;
  font-family: inherit;
  font-size: 14px;

  &::placeholder {
    color: var(--transparent-white-50);
  }

  &:focus {
    outline: none;
    border-color: var(--purple);
  }
}

.field-hint {
  color: rgba(255, 255, 255, 0.5);
  font-size: 11px;
  line-height: 1.5;
  margin: 0;

  strong {
    color: rgba(255, 255, 255, 0.8);
    font-weight: 500;
  }
}

.workspace-loading,
.workspace-error {
  color: rgba(255, 255, 255, 0.5);
  font-size: 13px;
  padding: 8px 0;
}

.workspace-error {
  color: #ffb74d;
}

.capability-checkboxes {
  display: flex;
  gap: 16px;
  padding: 4px 2px;
}

.capability-checkbox {
  display: flex;
  align-items: center;
  gap: 6px;
  color: #fff;
  font-size: 13px;
  cursor: pointer;

  input {
    accent-color: var(--purple);
  }

  &.disabled {
    color: rgba(255, 255, 255, 0.45);
    cursor: not-allowed;
  }
}

.consent-disclaimer {
  text-align: center;
  color: var(--transparent-white-50);
  font-size: 12px;
  line-height: 1.5;
}

.figma-status {
  align-items: flex-start !important;

  .info-label {
    padding-top: 6px;
  }

  .status-badge {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 2px 8px;
    border-radius: 4px;
    font-size: 12px;
    font-weight: 500;

    &.connected {
      background: rgba(76, 175, 80, 0.15);
      color: #4caf50;
    }
  }

  .figma-connect-wrapper {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 4px;
  }

  .connect-figma-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 4px 12px;
    border-radius: 4px;
    font-size: 12px;
    font-weight: 500;
    font-family: inherit;
    background: rgba(255, 152, 0, 0.1);
    border: 1px solid rgba(255, 152, 0, 0.5);
    color: #ffb74d;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
      background: rgba(255, 152, 0, 0.2);
      border-color: rgba(255, 152, 0, 0.7);
    }
  }

  .figma-helper-text {
    font-size: 11px;
    color: var(--transparent-white-50);
  }
}
</style>
